import _ from 'lodash'
import clx from 'classnames'
import { useRef, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Typography, Row, Col } from 'antd'
import { Modal, Tip, Button, Loader } from '@vzmi/shp_shared_compo'
import { CDN } from '@vzmi/shp_shared_compo/dist/constants/cdn.js'
import {
  FormRow,
  InputCountField,
  InputField,
  RadioGroupInputField,
  HyperLink
} from '../../../../Common/Form'
import { atLeast, filterHtmlTagAndSpace } from '../../../../Proposal/Common/ProposalForm/Shared/normalizers'
import { showOnlyNumber, showNoEmoji } from '../../../../../lib/formatter'
import {
  validateCouponDiscountCount,
  validateCouponTitle,
  validateCouponMinQtyDiscountAmount,
  validateCouponMinTotalDiscountAmount,
  validateCouponDuration
} from '../../../../Proposal/Common/ProposalForm/Shared/validation/fieldLevelValidator'
import { IconPopoverLabel } from '../../../../Proposal/Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import { ConnectedListingLink } from '../../../../Common/ListingLink/ListingLink'
import { Field, formDecorator } from '../../../../Common/forms'
import { getFormValues } from '../../../../Proposal/Common/ProposalForm/Shared/util'
import { dollarFormat } from '../../../../../../lib/renderUtil'
import { EMPTY_VALUE, API_ERROR_MAPPING } from '../../../../../constants/globalTypes'
import { RES_TYPE } from '../constants'
import { TARGET_AUDIENCE } from '../RankingTable/RankingTable'
import styles from './ECouponModal.css'

export const DISCOUNT_RULE_TYPES = {
  MINQTY: 'minQty',
  MINTOTAL: 'minTotal'
}

export const RADIO_DISCOUNT_RULE_TYPES = [
  { label: '', value: DISCOUNT_RULE_TYPES.MINQTY },
  { label: '', value: DISCOUNT_RULE_TYPES.MINTOTAL }
]

export const RADIO_IS_ALLOWED_ACTIVITY_TYPES = [
  { label: '是', value: true },
  { label: '否', value: false }
]

export const COUPON_FIELD = {
  TITLE: 'title',
  TARGET_AUDIENCE: 'targetAudience',
  DISCOUNT_COUNT: 'discountCount',
  DISCOUNT_RULE: 'discountRule',
  MIN_QTY_DISCOUNT_THRESHOLD: 'minQtyDiscountThreshold',
  MIN_QTY_DISCOUNT_AMOUNT: 'minQtyDiscountAmount',
  MIN_TOTAL_DISCOUNT_THRESHOLD: 'minTotalDiscountThreshold',
  MIN_TOTAL_DISCOUNT_AMOUNT: 'minTotalDiscountAmount',
  DURATION: 'duration',
  IS_ALLOWED_ACTIVITY: 'isAllowedActivity',
  COST: 'cost',
  PRICE: 'price',
  LISTING_ID: 'listingId',
  YESTERDAY_QUANTITY: 'yesterdayQuantity',
  QUANTITY: 'quantity'
}

const CONFIG = {
  form: 'ECOUPON_FORM'
}

export const DURATION_DEFAULT_VALUES = 3

export const MODAL_DEFAULT_VALUES = {
  [COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD]: 1,
  [COUPON_FIELD.DISCOUNT_RULE]: DISCOUNT_RULE_TYPES.MINQTY,
  [COUPON_FIELD.DURATION]: DURATION_DEFAULT_VALUES,
  [COUPON_FIELD.IS_ALLOWED_ACTIVITY]: true
}

const NormalHyperLinkComponent = props => {
  const { sharedLink } = props

  return (
    <div data-role='searchBlock'>
      請至
      <HyperLink
        href={sharedLink.scmQueryCampaign}
        target='_blank'
      >
        我的折價券
      </HyperLink>
      查看折價券發放與使用狀況。
    </div>
  )
}

const IdHyperLinkComponent = props => {
  const { successData, sharedLink } = props
  const idParam = `?id=${successData.discountId}`

  return (
    <HyperLink
      href={`${sharedLink.scmQueryCampaign}${idParam}`}
      target='_blank'
      data-role='discountId'
    >
      {successData.discountId}
    </HyperLink >
  )
}

export const SearchText = props => {
  const { successData = {}, hasIdStatus = false } = props
  const sharedLink = _.get(props, 'supplier.sharedLink', {})

  const HyperLinkComponent = useMemo(() => {
    if (hasIdStatus) {
      return IdHyperLinkComponent
    }

    return NormalHyperLinkComponent
  }, [hasIdStatus])

  return (
    <HyperLinkComponent
      successData={successData}
      sharedLink={sharedLink}
    />
  )
}

const SuccessForm = props => {
  const { successData } = props
  return (
    <>
      <h1 className={styles.titleText}>折價券已發送</h1>
      <h2 className={styles.paddingBottomSpace}>
        折價券序號：
        <SearchContainer
          successData={successData}
          hasIdStatus
        />
      </h2>
      <p>
        折價券(
        <span className={styles.boldText}>{`${successData.title}`}</span>
        )已發送給
        <span className={styles.boldText}>{` ${successData.discountCount} `}</span>
        位買家，
      </p>
      <p className={styles.link}>
        <SearchContainer />
      </p>
    </>
  )
}

const NoBuyerForm = () => {
  return (
    <>
      <h1 className={styles.titleText}>折價券未成功發送：</h1>
      <h1
        className={clx(styles.titleText, styles.paddingBottomSpace)}
        data-role='noBuyerTextBlock'
      >
        無可發送對象，買家可能已經完成結帳。
      </h1>
      <p className={styles.link}>
        <SearchContainer />
      </p>
    </>
  )
}

const ErrorForm = props => {
  const { couponsError = {} } = props
  const errorCode = _.get(couponsError, 'error.code')

  let errorText
  const code = Number(errorCode)
  switch (code) {
    // 折價券發送中
    case 40900002: {
      errorText = `${API_ERROR_MAPPING[code]}，等待幾分鐘後`
      break
    }
    case 42900003: {
      const previousCouponDeadline = _.flow(
        () => _.get(couponsError, 'error.detail[0].invalidValue', ''),
        // ex: ': 2023/11/17 09:46' -> '11/17 09:46'
        invalidValue => _.replace(invalidValue, /:\s\d{4}\//, '')
      )()
      errorText = `${API_ERROR_MAPPING[code]}${previousCouponDeadline}`
      break
    }
    default: {
      errorText = _.get(API_ERROR_MAPPING, code, '連線逾時')
      break
    }
  }

  return (
    <>
      <h1 className={styles.titleText}>{`${errorText}，`}</h1>
      <h1 className={clx(styles.titleText, styles.link)}>
        <SearchContainer />
      </h1>
    </>
  )
}

const CouponFormContent = props => {
  const {
    targetAudience,
    promotion,
    listing,
    isAllowedActivityStatus,
    numTotal,
    triggerFieldChange
  } = props
  const { listingId, listingName, yesterdayQuantity, quantity, couponPrice, image, cost, listingEndTs } = listing
  const { title, promotionEndTs } = promotion
  const isAbandonedCart = (targetAudience === TARGET_AUDIENCE.ABANDONED_CART)

  return (
    <>
      <FormRow
        className={styles.listingInfo}
        label={(
          <img
            className={styles.img}
            src={image}
          />
        )}
        data-role='listingInfo'
      >
        <div className={styles.name}>
          <ConnectedListingLink targetListingId={listingId}>
            {listingId}
          </ConnectedListingLink>
          <Typography.Paragraph
            className={styles.paragraph}
            strong
            ellipsis={{ rows: 1 }}
          >
            {listingName}
          </Typography.Paragraph>
        </div>
        <p
          className={styles.halfWidth}
          data-role='price'
        >
          {`目前售價：${dollarFormat(couponPrice)}`}
        </p>
        <p className={styles.halfWidth}>
          成本：
          <span
            className={styles.cost}
            data-role='cost'
          >
            {dollarFormat(showOnlyNumber(cost)) || EMPTY_VALUE}
          </span>
        </p>
        {
          (title && promotionEndTs) && (
            <>
              <div className={styles.flag}>活動</div>
              <div className={styles.promotions}>
                <p className={styles.title}>{title}</p>
                <p className={styles.promotionEndTs}>{`有效至${formatedEffectiveTime(promotionEndTs)}`}</p>
              </div>
            </>
          )
        }
      </FormRow>
      <FormRow
        label='折價券名稱'
        labelAlign='top'
        className={styles.resetFormMargin}
      >
        <Row align='middle'>
          <Col>
            <p>購物車專屬折價券-</p>
          </Col>
          <Col data-role='titleBlock'>
            <Field
              name={COUPON_FIELD.TITLE}
              type='text'
              className={styles.titleField}
              component={InputCountField}
              countLimit={41}
              validate={validateCouponTitle}
              normalizeOnBlur={filterHtmlTagAndSpace}
              format={showNoEmoji}
              showErrorOnActive
              mustShowFormError
            />
          </Col>
        </Row>
      </FormRow>
      <FormRow
        label={(
          <IconPopoverLabel
            popoverContent='送出折價券後，系統會自動剔除已經完成結帳之買家。'
          >
            發送對象
          </IconPopoverLabel>
        )}
        labelAlign='top'
        className={styles.resetFormMargin}
      >
        <Row>
          <Col span={24}>
            <Tip type='info'>
              折價券發送順序以加入時間近者優先。
            </Tip>
          </Col>
          <Row align='middle'>
            <Col data-role='discountCountBlock'>
              <Field
                key={targetAudience}
                name={COUPON_FIELD.DISCOUNT_COUNT}
                type='text'
                className={styles.discountCountField}
                component={InputField}
                validate={validateCouponDiscountCount(isAbandonedCart ? quantity : yesterdayQuantity)}
                parse={showOnlyNumber}
                normalizeOnBlur={atLeast(1)}
                showErrorOnActive
                mustShowFormError
              />
            </Col>
            <Col>
              {
                isAbandonedCart
                  ? (<p className={styles.defaultSpacing}>位截至昨日購物未結帳之買家</p>)
                  : (<p className={styles.defaultSpacing}>位昨日加入購物車之買家</p>)
              }
            </Col>
          </Row>
        </Row>
      </FormRow>
      <FormRow
        label='折抵方式'
        labelAlign='top'
        className={styles.resetFormMargin}
      >
        <Row>
          <Col span={2}>
            <Field
              name={COUPON_FIELD.DISCOUNT_RULE}
              component={RadioGroupInputField}
              buttonStyle='outline'
              options={RADIO_DISCOUNT_RULE_TYPES}
              className={styles.discountRuleField}
              onChange={onChangeDiscountRadioField(triggerFieldChange)}
              showErrorOnActive
              mustShowFormError
            />
          </Col>
          <Col span={22}>
            <Row
              align='middle'
              className={styles.discountBlock}
            >
              <Col>
                <p className={styles.defaultSpacing}>滿</p>
              </Col>
              <Col>
                <Field
                  name={COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD}
                  type='text'
                  component={InputField}
                  className={styles.discountThresholdField}
                  onChange={onChangeDiscountInputField(triggerFieldChange)}
                  parse={showOnlyNumber}
                  showErrorOnActive
                  mustShowFormError
                />
              </Col>
              <Col>
                <p className={styles.defaultSpacing}>件，折</p>
              </Col>
              <Col data-role='minQtyDiscountAmountBlock'>
                <Field
                  name={COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT}
                  type='text'
                  component={InputField}
                  className={styles.discountAmountField}
                  onChange={onChangeDiscountInputField(triggerFieldChange)}
                  validate={validateCouponMinQtyDiscountAmount(cost)}
                  parse={showOnlyNumber}
                  showErrorOnActive
                  mustShowFormError
                />
              </Col>
              <Col>
                <p className={styles.defaultSpacing}>元。</p>
              </Col>
            </Row>
            <Row align='middle'>
              <Col>
                <p className={styles.defaultSpacing}>滿</p>
              </Col>
              <Col>
                <Field
                  name={COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD}
                  type='text'
                  component={InputField}
                  className={styles.discountThresholdField}
                  onChange={onChangeDiscountInputField(triggerFieldChange)}
                  parse={showOnlyNumber}
                  showErrorOnActive
                  mustShowFormError
                />
              </Col>
              <Col>
                <p className={styles.defaultSpacing}>元，折</p>
              </Col>
              <Col data-role='minTotalDiscountAmountBlock'>
                <Field
                  name={COUPON_FIELD.MIN_TOTAL_DISCOUNT_AMOUNT}
                  type='text'
                  component={InputField}
                  className={styles.discountAmountField}
                  onChange={onChangeDiscountInputField(triggerFieldChange)}
                  validate={validateCouponMinTotalDiscountAmount(cost, couponPrice)}
                  parse={showOnlyNumber}
                  showErrorOnActive
                  mustShowFormError
                />
              </Col>
              <Col>
                <p className={styles.defaultSpacing}>元。</p>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col>
            <p
              className={styles.dollarTotal}
              data-role='numTotalBlock'
            >
              {`預計最高將收取費用 ${dollarFormat(numTotal)}元。`}
            </p>
          </Col>
        </Row>
      </FormRow >
      <FormRow
        label={(
          <IconPopoverLabel
            popoverContent={(
              <>
                • 發送後即刻生效，結束時間為您設定的期限
                <br />
                • 舉例：當下是 9/1 10:00, 您設定2日，則折價券有效期間為 9/1 10:00 ~ 9/3: 9:59
                <br />
                • 送出至發送可能會有5分鐘時間差
              </>
            )}
          >
            有效天數
          </IconPopoverLabel>
        )}
        labelAlign='top'
        className={styles.resetFormMargin}
      >
        <Row>
          <Col span={24}>
            <Tip type='info'>
              發送後即刻生效，結束時間為您設定的期限。
            </Tip>
          </Col>
          <Row align='middle'>
            <Col
              className={styles.durationBlock}
              data-role='durationBlock'
            >
              <Field
                name={COUPON_FIELD.DURATION}
                type='text'
                component={InputField}
                className={styles.durationField}
                parse={showOnlyNumber}
                validate={validateCouponDuration(listingEndTs)}
                normalizeOnBlur={atLeast(DURATION_DEFAULT_VALUES)}
                showErrorOnActive
                mustShowFormError
              />
            </Col>
            <Col>
              <p className={styles.defaultSpacing}>日</p>
            </Col>
          </Row>
        </Row>
      </FormRow>
      <FormRow
        label='與活動同步折抵'
        labelAlign='top'
        className={styles.resetFormMargin}
      >
        <Row>
          <Col span={24}>
            <Field
              data-role='isAllowedActivity'
              name={COUPON_FIELD.IS_ALLOWED_ACTIVITY}
              component={RadioGroupInputField}
              buttonStyle='outline'
              options={RADIO_IS_ALLOWED_ACTIVITY_TYPES}
              className={styles.isAllowedActivity}
              showErrorOnActive
              mustShowFormError
            />
            <div
              className={styles.isAllowedActivityText}
              data-role='isAllowedActivityBlock'
            >
              {isAllowedActivityStatus
                ? '即雙享券，折價券可以與活動疊加折抵訂單金額。'
                : '若賣場有活動，則活動與折價券無法同時折抵。'
              }
            </div>
          </Col>
        </Row>
      </FormRow>
    </>
  )
}

export const getResultIcon = isSuccess => {
  if (isSuccess) {
    return `${CDN}/img/img_proposal_success.svg`
  } else {
    return `${CDN}/supplier/img_searchnodata_1567392001662.svg`
  }
}

const CouponFormResponse = props => {
  const { resType, successData, couponsError } = props
  const successType = resType === RES_TYPE.SUCCESS
  const errorType = (resType === RES_TYPE.ERROR || resType === RES_TYPE.DISCOUNT_COUNT_ZERO)
  const imgUrl = useMemo(() => {
    return getResultIcon(!errorType)
  }, [errorType])

  const ResForm = useMemo(() => {
    if (resType === RES_TYPE.SUCCESS) {
      return SuccessForm
    } else if (resType === RES_TYPE.DISCOUNT_COUNT_ZERO) {
      return NoBuyerForm
    } else {
      return ErrorForm
    }
  }, [resType])

  return (
    <Row
      justify='center'
      align='middle'
    >
      <Col
        span={24}
        className={styles.content}
      >
        <img
          src={imgUrl}
          className={clx(
            { [styles.normalImage]: errorType },
            { [styles.customImage]: successType }
          )}
        />
        <ResForm
          successData={successData}
          couponsError={couponsError}
        />
      </Col>
    </Row>
  )
}

// 判斷欄位名稱是否為 batch 批次折價券發送的情況，fieldName 會是 `batch_{listingId}_{fieldName}`
export const getIsBatch = (originFieldName) => {
  return originFieldName.startsWith('batch_')
}

// 取得 batch 欄位名稱為區別不同賣場使用的前綴
// 例如：batch_12345_minQtyDiscountThreshold -> batch_12345_
export const getBatchPrefix = (originFieldName) => {
  const splitWithUnderscore = originFieldName.split('_')
  return splitWithUnderscore.slice(0, 2).concat('').join('_')
}

// 當選擇不同 折抵方式，另一邊須清空
export const onChangeDiscountRadioField = triggerFieldChange => (e, value, previousValue, originFieldName) => {
  const isBatch = getIsBatch(originFieldName)
  const batchPrefix = isBatch ? getBatchPrefix(originFieldName) : ''

  const isMinQty = value === DISCOUNT_RULE_TYPES.MINQTY
  const thresoldFieldName = isMinQty
    ? COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD
    : COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD
  const amountFieldName = isMinQty
    ? COUPON_FIELD.MIN_TOTAL_DISCOUNT_AMOUNT
    : COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT

  triggerFieldChange(`${batchPrefix}${thresoldFieldName}`, null)
  triggerFieldChange(`${batchPrefix}${amountFieldName}`, null)
}

export const onChangeDiscountInputField = triggerFieldChange => (e, newValue, previousValue, originFieldName) => {
  const isBatch = getIsBatch(originFieldName)
  const fieldName = isBatch ? originFieldName.split('_')[2] : originFieldName
  const batchPrefix = isBatch ? getBatchPrefix(originFieldName) : ''
  // 當選擇不同 折抵方式，另一邊須清空
  const isMinQtyDiscount = fieldName === COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD || fieldName === COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT
  const thresoldFieldName = isMinQtyDiscount
    ? COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD
    : COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD
  const amountFieldName = isMinQtyDiscount
    ? COUPON_FIELD.MIN_TOTAL_DISCOUNT_AMOUNT
    : COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT
  triggerFieldChange(`${batchPrefix}${thresoldFieldName}`, null)
  triggerFieldChange(`${batchPrefix}${amountFieldName}`, null)

  // 依當前的選擇改變折抵方式 radio type
  const ruleFieldValue = isMinQtyDiscount
    ? DISCOUNT_RULE_TYPES.MINQTY
    : DISCOUNT_RULE_TYPES.MINTOTAL
  triggerFieldChange(`${batchPrefix}${COUPON_FIELD.DISCOUNT_RULE}`, ruleFieldValue)

  // 當滿件/元值為空時，清空折抵金額
  if (fieldName === COUPON_FIELD.MIN_QTY_DISCOUNT_THRESHOLD) {
    if (_.isEmpty(_.toString(newValue))) {
      triggerFieldChange(`${batchPrefix}${COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT}`, null)
    }
  } else if (fieldName === COUPON_FIELD.MIN_TOTAL_DISCOUNT_THRESHOLD) {
    if (_.isEmpty(_.toString(newValue))) {
      triggerFieldChange(`${batchPrefix}${COUPON_FIELD.MIN_TOTAL_DISCOUNT_AMOUNT}`, null)
    }
  }
}

export const formatedEffectiveTime = (dateString) => {
  const dateTime = new Date(dateString)

  if (dateTime.getHours() < 12) {
    return `${dateString} AM`
  } else {
    return `${dateString} PM`
  }
}

const ECouponModal = props => {
  const {
    listingsInfoData = {},
    eCouponVisible,
    onCloseModal,
    triggerFieldChange,
    reduxFormState,
    onSubmitModal,
    formVisible,
    successData,
    resType,
    initialize,
    isLoading,
    couponsError = {}
  } = props
  const { listing = {}, promotion = {}, targetAudience } = listingsInfoData
  const { listingId, listingName, quantity, yesterdayQuantity, couponImage } = listing
  const prevVisible = useRef(false)
  const formValues = getFormValues(reduxFormState) || {}
  const syncErrorsStatus = !_.isEmpty(_.get(reduxFormState, 'syncErrors'))
  const isAbandonedCart = (targetAudience === TARGET_AUDIENCE.ABANDONED_CART)

  const {
    [COUPON_FIELD.MIN_QTY_DISCOUNT_AMOUNT]: minQtyDiscountAmount = 0,
    [COUPON_FIELD.MIN_TOTAL_DISCOUNT_AMOUNT]: minTotalDiscountAmount = 0,
    [COUPON_FIELD.IS_ALLOWED_ACTIVITY]: isAllowedActivityStatus = true,
    [COUPON_FIELD.DISCOUNT_COUNT]: discountCount = 1
  } = formValues

  useEffect(() => {
    const defaultValues = {
      ...MODAL_DEFAULT_VALUES,
      [COUPON_FIELD.TITLE]: listingName,
      [COUPON_FIELD.TARGET_AUDIENCE]: targetAudience,
      [COUPON_FIELD.DISCOUNT_COUNT]: isAbandonedCart ? quantity : yesterdayQuantity,
      [COUPON_FIELD.QUANTITY]: quantity
    }

    if (prevVisible.current === eCouponVisible) {
      return
    }
    if (!eCouponVisible) {
      return
    }
    initialize(defaultValues)
  }, [initialize, listingName, yesterdayQuantity, quantity, targetAudience, isAbandonedCart, eCouponVisible])

  const CouponForm = useMemo(() => {
    if (formVisible) {
      return CouponFormContent
    }

    return CouponFormResponse
  }, [formVisible])

  const numTotal = discountCount * (minQtyDiscountAmount || minTotalDiscountAmount)

  return (
    <Modal
      data-role='couponModal'
      centered
      className={styles.modal}
      visible={eCouponVisible}
      title='發放折價券'
      width={720}
      onCancel={() => {
        onCloseModal()
      }}
      footer={(
        <Row
          justify="center"
          align="middle"
          className={styles.footerBlock}
        >
          <Col>
            <Row
              gutter={[16, 16]}
              justify="center"
            >
              {formVisible && (
                <Col>
                  <Button
                    disabled={isLoading}
                    onClick={() => {
                      const newFormValues = {
                        ...formValues,
                        yesterdayQuantity,
                        listingId,
                        imageUrl: couponImage
                      }
                      onSubmitModal(newFormValues, syncErrorsStatus)
                    }}
                    size='large'
                    data-role='submitCoupon'
                  >
                    送出折價券
                  </Button>
                </Col>
              )}
              <Col>
                <Button
                  onClick={() => {
                    onCloseModal()
                  }}
                  size='large'
                  color={formVisible ? 'gray' : 'blue'}
                >
                  {formVisible ? '取消' : '關閉視窗'}
                </Button>
              </Col>
            </Row>
            {formVisible && (
              <Row justify="center">
                <Col
                  span={24}
                  align="middle"
                  className={styles.footerTextBlock}
                >
                  <p className={styles.footerText}>「送出折價券」表示貴公司同意產生之折價券費用將由貴公司 100% 負擔，</p>
                  <p className={styles.footerText}>{`預計最高將收取費用 ${dollarFormat(numTotal)}元，雅虎將自動產生費用並於貨款中扣除。`}</p>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      )}
    >
      <Loader loaded={!isLoading}>
        <CouponForm
          targetAudience={targetAudience}
          resType={resType}
          successData={successData}
          listing={listing}
          promotion={promotion}
          isAllowedActivityStatus={isAllowedActivityStatus}
          numTotal={numTotal}
          couponsError={couponsError}
          triggerFieldChange={triggerFieldChange}
        />
      </Loader>
    </Modal>
  )
}

const mapStateToProps = state => ({
  supplier: state.supplier
})

const SearchContainer = connect(
  mapStateToProps
)(SearchText)

export default formDecorator(CONFIG)(ECouponModal)
